import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { DashboardLayout } from "../common/page_component/index.js";
import Alert from "../common/Alert.js";
import Button from "../common/Button.js";
import agentDirectoryService from "../../services/agentDirectoryService.js";
import agentPartnershipService from "../../services/agentPartnershipService.js";
import { useAuth } from "../../context/AuthContext.js";
import isExternalNetworkEnabled from "../../utils/networkMode.js";
import { useTranslation } from 'react-i18next';


const FindAgent = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [agents, setAgents] = useState([]);
  const [requestingId, setRequestingId] = useState(null);
  const networkEnabled = isExternalNetworkEnabled(user);

  const runSearch = useCallback(async (searchCity, searchState, searchName) => {
    try {
      setLoading(true);
      const response = await agentDirectoryService.search({
        city: (searchCity || "").trim(),
        state: (searchState || "").trim(),
        name: (searchName || "").trim(),
      });
      const payload = response.data || {};
      setAgents(payload.data || []);
    } catch (error) {
      Alert.error(error.response?.data?.error || "Search failed");
      setAgents([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!networkEnabled) {
      navigate("/admin-dashboard");
    }
  }, [networkEnabled, navigate]);

  useEffect(() => {
    if (networkEnabled) {
      runSearch("", "", "");
    }
  }, [networkEnabled, runSearch]);

  if (!networkEnabled) {
    return null;
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    await runSearch(city, state, name);
  };

  const resolveServiceCity = (agent) => {
    const fromSearch = city.trim();
    if (fromSearch) return fromSearch;
    if (agent.city?.trim()) return agent.city.trim();
    const fromProfile = (agent.service_cities || []).find((entry) => entry && String(entry).trim());
    return fromProfile ? String(fromProfile).trim() : "";
  };

  const handleRequest = async (agent) => {
    const serviceCity = resolveServiceCity(agent);
    if (!serviceCity) {
      Alert.error("This agent has no service city on file. Enter a service city in the search field first.");
      return;
    }

    const confirmed = await Alert.confirm(
      `Send a partnership request to ${agent.name} for ${serviceCity}? They will be notified to accept or decline.`,
      "Send Partnership Request"
    );
    if (!confirmed) return;

    try {
      setRequestingId(agent.user_id);
      await agentPartnershipService.request({
        agent_user_id: agent.user_id,
        service_city: serviceCity,
        request_message: `Partnership request for ${serviceCity}`,
      });
      Alert.success("Partnership request sent");
      await runSearch(city, state, name);
    } catch (error) {
      Alert.error(error.response?.data?.error || "Failed to send request");
    } finally {
      setRequestingId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-semibold">{t('pages.agentNetwork.findAgent')}</h1>
          <Button variant="secondary" onClick={() => navigate("/partner-agents")}>Back</Button>
        </div>
        <p className="text-sm text-gray-600">
          Discover agents from <strong>internal and external</strong> companies. Leave city blank to list all, or filter by city (e.g. Mapusa, Mumbai). When sending a request, the agent&apos;s city is used unless you enter a different service city above.
        </p>
        <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-5 gap-3 rounded-lg border bg-white p-4">
          <input className="rounded-md border px-3 py-2 text-sm" placeholder="Service city (optional)" value={city} onChange={(e) => setCity(e.target.value)} />
          <input className="rounded-md border px-3 py-2 text-sm" placeholder="State (optional)" value={state} onChange={(e) => setState(e.target.value)} />
          <input className="rounded-md border px-3 py-2 text-sm" placeholder="Agent name" value={name} onChange={(e) => setName(e.target.value)} />
          <Button type="submit" variant="primary" disabled={loading}>{loading ? "Searching..." : t("common.search")}</Button>
          <Button type="button" variant="secondary" disabled={loading} onClick={() => { setCity(""); setState(""); setName(""); runSearch("", "", ""); }}>
            Show all agents
          </Button>
        </form>
        <div className="overflow-x-auto rounded-lg border bg-white">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left">
              <tr>
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">City</th>
                <th className="px-4 py-3">{t('forms.state')}</th>
                <th className="px-4 py-3">{t('common.company')}</th>
                <th className="px-4 py-3">Partnership</th>
                <th className="px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {agents.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-6 text-center text-gray-500">
                  No agents found for this search. Try another city or click Show all agents.
                </td></tr>
              ) : agents.map((agent) => (
                <tr key={agent.user_id} className="border-t">
                  <td className="px-4 py-3">{agent.name}</td>
                  <td className="px-4 py-3">{agent.city || '—'}</td>
                  <td className="px-4 py-3">{agent.state || '—'}</td>
                  <td className="px-4 py-3">
                    <div>{agent.home_company_name}</div>
                    <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium ${
                      agent.home_company_network_mode === 'EXTERNAL'
                        ? 'bg-violet-100 text-violet-800'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {agent.home_company_network_mode || 'INTERNAL'}
                    </span>
                  </td>
                  <td className="px-4 py-3 capitalize">{agent.partnership_status}</td>
                  <td className="px-4 py-3">
                    {agent.partnership_status === "none" && (
                      <Button size="sm" variant="primary" disabled={requestingId === agent.user_id} onClick={() => handleRequest(agent)}>
                        Send Request
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FindAgent;
